import { logger } from '@vagabondinternet/logging';
import { WordsHappenButton } from '@words-happen/ui-components';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { authErrorCode, browserIdentitySession, type IdentitySession } from '../../api';
import { MessageScreen, messageScreenActionsClassName } from '../../components';
import { type BrowserNavigation, routeState, useAuthNavigation } from '../../navigation';
import { ROUTES } from '../../routes';
import { HANDOFF_MESSAGES, HandoffScreen, useSessionHandoff } from '../../session';
import { useResend } from '../use-resend';
import { VERIFICATION_MESSAGES, VERIFICATION_RESEND_MESSAGES, verificationErrorMessage } from './verification-messages';
import { VerifyEmailScreen } from './verify-email-screen';

export interface VerifyEmailPageProps {
  /** Firebase's signed-in user and the API's session endpoints. Defaults to the browser's. */
  session?: IdentitySession;
  /** How a verified player leaves the app. Defaults to the window's history and location. */
  navigation?: BrowserNavigation;
}

type Phase =
  /** Finding the signed-in player. */
  | 'loading'
  | 'ready'
  /** No one is signed in, and an automatic redirect already brought the player here. */
  | 'signed-out';

/**
 * /verify-email: "Check your email". The address comes from history state, else from the signed-in player; with no
 * one signed in, the page goes to /sign-in. It checks on arrival, when the tab regains focus and on "I've verified";
 * every check reloads the user and refreshes their token, and a verified player is handed off to the game. The
 * session API refusing an unverified address (EMAIL_NOT_VERIFIED) is shown here as an error, never redirected.
 */
export const VerifyEmailPage: React.FC<VerifyEmailPageProps> = ({ session = browserIdentitySession, navigation }) => {
  const { switchTo, leave, returnTo, continueUrl } = useAuthNavigation(navigation);
  const { state } = useLocation();
  const [arrival] = useState(() => routeState(state));
  const handoff = useSessionHandoff({ session, returnTo, leave });
  const [phase, setPhase] = useState<Phase>('loading');
  const [email, setEmail] = useState<string | null>(arrival.email ?? null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | undefined>(
    arrival.verificationSent === false ? VERIFICATION_MESSAGES.notSent : undefined,
  );
  const checkInFlight = useRef(false);
  // Whether the check in flight should report its outcome: the player asked for it.
  const manualCheck = useRef(false);
  // React StrictMode mounts twice in development; the arrival check runs once.
  const arrived = useRef(false);

  const resend = useResend(
    useCallback(() => session.resendVerification(continueUrl), [session, continueUrl]),
    { messages: VERIFICATION_RESEND_MESSAGES, coolingDown: arrival.verificationSent === true },
  );

  const toSignIn = useCallback(() => {
    if (arrival.redirected) {
      setPhase('signed-out');
      return;
    }
    switchTo(
      ROUTES.signIn,
      { ...(arrival.email ? { email: arrival.email } : {}), redirected: true },
      { replace: true },
    );
  }, [arrival, switchTo]);

  /**
   * Reloads the player; hands a verified one off. One check runs at a time; a manual one ("I've verified") joins any
   * check in flight, and only manual checks report a player who is still unverified.
   */
  const check = useCallback(
    async (manual: boolean) => {
      if (manual) {
        manualCheck.current = true;
        setChecking(true);
        setError(undefined);
      }
      if (checkInFlight.current) {
        return;
      }
      checkInFlight.current = true;
      try {
        const user = await session.refreshUser();
        if (!user) {
          toSignIn();
          return;
        }
        setEmail((current) => current ?? user.email);
        if (user.emailVerified) {
          // Leaves the app on success; a failure stays here, shown as an error.
          await handoff.complete();
          setPhase('ready');
          return;
        }
        setPhase('ready');
        if (manualCheck.current) {
          setError(VERIFICATION_MESSAGES.stillUnverified);
        }
      } catch (cause) {
        logger.error({ message: 'Checking email verification failed', code: authErrorCode(cause) });
        setPhase('ready');
        if (manualCheck.current) {
          setError(verificationErrorMessage(cause, VERIFICATION_MESSAGES.checkFailed));
        }
      } finally {
        checkInFlight.current = false;
        manualCheck.current = false;
        setChecking(false);
      }
    },
    [session, handoff, toSignIn],
  );

  useEffect(() => {
    if (arrived.current) {
      return;
    }
    arrived.current = true;
    if (!session.mayBeSignedIn()) {
      toSignIn();
      return;
    }
    void check(false);
  }, [session, check, toSignIn]);

  useEffect(() => {
    if (phase !== 'ready') {
      return;
    }
    const recheck = () => {
      if (document.visibilityState !== 'hidden') {
        void check(false);
      }
    };
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);
    return () => {
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
    };
  }, [phase, check]);

  const signOut = async () => {
    try {
      await session.signOut();
    } catch (cause) {
      logger.warn({ message: 'Signing out of Firebase failed', code: authErrorCode(cause) });
    }
    switchTo(ROUTES.signIn, undefined, { replace: true });
  };

  const handoffError =
    handoff.state.phase === 'failed'
      ? handoff.state.failure === 'email-not-verified'
        ? VERIFICATION_MESSAGES.stillUnverified
        : HANDOFF_MESSAGES[handoff.state.failure]
      : undefined;

  const screen = () => {
    if (handoff.state.phase === 'working') {
      return <HandoffScreen handoff={handoff} />;
    }
    if (phase === 'signed-out') {
      return (
        <MessageScreen
          title="Sign in to carry on"
          mood="focused"
          action={
            <div className={messageScreenActionsClassName}>
              <WordsHappenButton variant="coral" size="lg" onClick={() => switchTo(ROUTES.signIn)}>
                Sign in
              </WordsHappenButton>
            </div>
          }
        >
          <p>Sign in to check whether your email address is verified.</p>
        </MessageScreen>
      );
    }
    if (phase === 'loading' && !email) {
      return (
        <MessageScreen title="One moment…" mood="focused">
          <p role="status">Checking your account.</p>
        </MessageScreen>
      );
    }
    return (
      <VerifyEmailScreen
        email={email}
        resend={resend}
        checking={checking}
        error={error ?? (checking ? undefined : handoffError)}
        onCheck={() => void check(true)}
        onSignOut={() => void signOut()}
      />
    );
  };

  return screen();
};
